import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import DarkNav from '../components/dark-nav'
import Breadcrumbs from '../components/breadcrumbs'
import Footer from '../components/footer'
import { EmailToSmsIcon } from './integrations-hub'
import { getCanonicalUrl } from '../utils/canonical'
import { trackGetStartedClick, trackSignUpConversion } from '../utils/googleAds'
import './integrations-hub.css'
import './developers.css'

const REGISTER_URL = 'https://sms.dataflows.com.au/register'
const DOCS_URL = 'https://docs.dataflows.com.au/'

const PAGE_TITLE = 'SMS API for Developers | DataFlows'
const PAGE_DESCRIPTION = 'Add SMS to your app with the DataFlows API. Send texts, manage contacts and connect AI assistants. Code samples in cURL, PHP, Node and Python.'

const samples = [
  {
    id: 'curl',
    label: 'cURL',
    code: `curl -X POST https://sms.dataflows.com.au/api/http/sms/send \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"recipient":"61491570156","sender_id":"YOUR_SENDER_ID","message":"Hello from DataFlows"}'`,
  },
  {
    id: 'php',
    label: 'PHP',
    code: `$ch = curl_init('https://sms.dataflows.com.au/api/http/sms/send');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer YOUR_API_TOKEN',
        'Content-Type: application/json',
        'Accept: application/json',
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'recipient' => '61491570156',
        'sender_id' => 'YOUR_SENDER_ID',
        'message'   => 'Hello from DataFlows',
    ]),
]);
$result = json_decode(curl_exec($ch), true);
if ($result['status'] === 'error') {
    throw new Exception($result['message']);
}`,
  },
  {
    id: 'node',
    label: 'Node',
    note: 'Node 18 or later',
    code: `const res = await fetch('https://sms.dataflows.com.au/api/http/sms/send', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  body: JSON.stringify({
    recipient: '61491570156',
    sender_id: 'YOUR_SENDER_ID',
    message: 'Hello from DataFlows',
  }),
});
const result = await res.json();
if (result.status === 'error') throw new Error(result.message);`,
  },
  {
    id: 'python',
    label: 'Python',
    code: `import requests

result = requests.post(
    'https://sms.dataflows.com.au/api/http/sms/send',
    headers={'Authorization': 'Bearer YOUR_API_TOKEN', 'Accept': 'application/json'},
    json={
        'recipient': '61491570156',
        'sender_id': 'YOUR_SENDER_ID',
        'message': 'Hello from DataFlows',
    },
).json()
if result['status'] == 'error':
    raise Exception(result['message'])`,
  },
]

const glyph = (paths) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
)

const apiReference = [
  {
    name: 'Authentication',
    hash: 'sec-auth',
    description: 'Get your API token and send it as a Bearer header or an api_token field.',
    icon: glyph(<><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9M17 6l3 3M14 9l2 2" /></>),
  },
  {
    name: 'Send SMS',
    hash: 'sec-a-sms',
    description: 'Send an SMS, list the messages you sent and view a single message.',
    icon: glyph(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />),
  },
  {
    name: 'Account and balance',
    hash: 'sec-a-account',
    description: 'View your profile, check your credit balance and list your sender IDs.',
    icon: glyph(<><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M16 15h2" /></>),
  },
  {
    name: 'Contact groups',
    hash: 'sec-a-groups',
    description: 'List, create, view, update and delete contact groups.',
    icon: glyph(<><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c2 .7 3 2.9 3 6" /></>),
  },
  {
    name: 'Contacts',
    hash: 'sec-a-contacts',
    description: 'List, add, view, update and delete the contacts in a group.',
    icon: glyph(<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></>),
  },
  {
    name: 'Responses and errors',
    hash: 'sec-a-responses',
    description: 'The standard response format and the HTTP status codes you may see.',
    icon: glyph(<><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></>),
  },
]

const aiAssistants = [
  {
    name: 'ChatGPT',
    path: '/integrations/chatgpt',
    description: 'Send, schedule and track SMS by asking ChatGPT in plain language.',
    icon: <img src="/logos/icons/chatgpt.png" alt="ChatGPT" width="28" height="28" />,
  },
  {
    name: 'Claude',
    path: '/integrations/claude',
    description: 'Send, schedule and track SMS by asking Claude in plain language.',
    icon: <img src="https://cdn.simpleicons.org/claude" alt="Claude" width="28" height="28" />,
  },
  {
    name: 'Gemini CLI',
    path: '/integrations/gemini',
    description: 'Send, schedule and track SMS from Gemini CLI in plain language.',
    icon: <img src="https://cdn.simpleicons.org/googlegemini" alt="Gemini" width="28" height="28" />,
  },
]

const integrations = [
  {
    name: 'Zapier',
    path: '/integrations/zapier',
    description: 'Connect DataFlows to thousands of apps with no-code Zapier workflows.',
    icon: <img src="https://cdn.simpleicons.org/zapier" alt="Zapier" width="28" height="28" />,
  },
  {
    name: 'Power Automate',
    path: '/integrations/power-automate',
    description: 'Send SMS from your flows with the certified DataFlows SMS connector.',
    icon: <img src="/logos/icons/power-automate.png" alt="Microsoft Power Automate" width="28" height="28" />,
  },
  {
    name: 'Microsoft Teams',
    path: '/integrations/microsoft-teams',
    description: 'Send and receive SMS directly from Microsoft Teams.',
    icon: <img src="/logos/icons/teams.png" alt="Microsoft Teams" width="28" height="28" />,
  },
  {
    name: 'Shopify',
    path: '/integrations/shopify',
    description: 'Automate order, shipping and abandoned cart SMS for your store.',
    icon: <img src="https://cdn.simpleicons.org/shopify" alt="Shopify" width="28" height="28" />,
  },
  {
    name: 'WordPress and WooCommerce',
    path: '/integrations/wordpress',
    description: 'Send SMS notifications from your WordPress or WooCommerce site.',
    icon: <img src="https://cdn.simpleicons.org/wordpress/white" alt="WordPress" width="28" height="28" />,
  },
  {
    name: 'HighLevel',
    path: '/integrations/gohighlevel',
    description: 'Connect SMS automation to your HighLevel CRM, pipelines and workflows.',
    icon: <img src="/logos/icons/gohighlevel.png" alt="HighLevel" width="28" height="28" />,
  },
  {
    name: 'Cliniko',
    path: '/integrations/cliniko',
    description: 'Automate appointment reminders and patient SMS for Cliniko practices.',
    icon: <img src="/logos/icons/cliniko.png" alt="Cliniko" width="28" height="28" />,
  },
  {
    name: 'Supabase',
    path: '/integrations/supabase',
    description: 'Send Supabase Auth phone login codes by SMS through DataFlows.',
    icon: <img src="https://cdn.simpleicons.org/supabase" alt="Supabase" width="28" height="28" />,
  },
  {
    name: 'Auth0',
    path: '/integrations/auth0',
    description: 'Add SMS one-time passcodes and multi-factor authentication to Auth0 logins.',
    icon: <img src="https://cdn.simpleicons.org/auth0" alt="Auth0" width="28" height="28" />,
  },
  {
    name: 'Email to SMS',
    path: '/integrations/email-to-sms',
    description: 'Send an SMS by emailing the mobile number at smsmail.dataflows.com.au, from any email app.',
    icon: <EmailToSmsIcon />,
  },
]

const repos = [
  {
    name: 'supabase-sms-hook',
    language: 'TypeScript',
    description: 'Send Supabase Auth phone login codes through the DataFlows API.',
  },
  {
    name: 'auth0-sms-action',
    language: 'JavaScript',
    description: 'Send Auth0 multi-factor and one-time codes through the DataFlows API.',
  },
  {
    name: 'dataflows-sms-claude-plugin',
    description: 'The DataFlows SMS plugin for Claude.',
  },
  {
    name: 'dataflows-sms-gemini',
    description: 'The DataFlows SMS extension for Gemini CLI.',
  },
]

const GitHubIcon = () => (
  <img src="https://cdn.simpleicons.org/github/white" alt="GitHub" width="26" height="26" />
)

const CardGrid = ({ items }) => (
  <div className="ih-grid">
    {items.map((item) => (
      <Link key={item.path} to={item.path} className="ih-card">
        <div className="ih-card-icon">{item.icon}</div>
        <h3 className="ih-card-title">{item.name}</h3>
        <p className="ih-card-desc">{item.description}</p>
        <span className="ih-card-arrow">→</span>
      </Link>
    ))}
  </div>
)

const Quickstart = () => {
  const [active, setActive] = useState(samples[0].id)
  const [copied, setCopied] = useState(false)
  const sample = samples.find((s) => s.id === active)

  const copy = () => {
    navigator.clipboard.writeText(sample.code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="dev-code">
      <div className="dev-code-head">
        <div className="dev-tabs" role="tablist" aria-label="Code samples">
          {samples.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === active}
              className={`dev-tab${s.id === active ? ' dev-tab--active' : ''}`}
              onClick={() => { setActive(s.id); setCopied(false) }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button type="button" className="dev-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      {sample.note && <div className="dev-code-note">{sample.note}</div>}
      <pre className="dev-pre" role="tabpanel"><code>{sample.code}</code></pre>
    </div>
  )
}

const GetApiKeyButton = () => (
  <a
    href={REGISTER_URL}
    target="_blank"
    rel="noreferrer noopener"
    className="ih-cta-button"
    onClick={() => { trackGetStartedClick(); trackSignUpConversion() }}
  >
    Get a free API key
  </a>
)

const Developers = () => {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Developers' },
  ]

  return (
    <div className="ih-container">
      <DarkNav />
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <link rel="canonical" href={getCanonicalUrl('/developers')} />
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dataflows.com.au/developers" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dataflows.com.au/' },
            { '@type': 'ListItem', position: 2, name: 'Developers', item: 'https://dataflows.com.au/developers' },
          ],
        })}</script>
      </Helmet>

      <div className="ih-breadcrumbs">
        <div className="ih-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="ih-hero">
        <div className="ih-inner">
          <span className="ih-badge">Developers</span>
          <h1 className="ih-title">Add SMS to your app</h1>
          <p className="ih-subtitle">
            Send texts, manage contacts and connect AI assistants with the DataFlows API.
          </p>
          <div className="dev-hero-actions">
            <GetApiKeyButton />
            <a href={DOCS_URL} target="_blank" rel="noreferrer noopener" className="dev-btn-outline">
              Read the docs
            </a>
          </div>
          <p className="dev-smallprint">
            The free trial includes 3 SMS credits, sent to numbers you verify.
            API limit: 600 requests per minute per token.
          </p>
        </div>
      </section>

      <section className="ih-grid-section">
        <div className="ih-inner dev-narrow">
          <h2 className="ih-grid-title">Quickstart</h2>
          <Quickstart />
          <p className="dev-status-note">
            Check the status field in every response. Most errors still return HTTP 200.
            See <a href={`${DOCS_URL}#sec-a-responses`} target="_blank" rel="noreferrer noopener">Responses and errors</a>.
          </p>
        </div>
      </section>

      <section className="ih-grid-section dev-divided">
        <div className="ih-inner">
          <h2 className="ih-grid-title">API reference</h2>
          <div className="ih-grid dev-grid-3">
            {apiReference.map((item) => (
              <a
                key={item.hash}
                href={`${DOCS_URL}#${item.hash}`}
                target="_blank"
                rel="noreferrer noopener"
                className="ih-card"
              >
                <div className="ih-card-icon">{item.icon}</div>
                <h3 className="ih-card-title">{item.name}</h3>
                <p className="ih-card-desc">{item.description}</p>
                <span className="ih-card-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="ih-grid-section dev-divided">
        <div className="ih-inner">
          <h2 className="ih-grid-title">AI assistants</h2>
          <CardGrid items={aiAssistants} />
        </div>
      </section>

      <section className="ih-grid-section dev-divided">
        <div className="ih-inner">
          <h2 className="ih-grid-title">Integrations</h2>
          <CardGrid items={integrations} />
        </div>
      </section>

      <section className="ih-grid-section dev-divided">
        <div className="ih-inner">
          <h2 className="ih-grid-title">Open source</h2>
          <div className="ih-grid">
            {repos.map((repo) => (
              <a
                key={repo.name}
                href={`https://github.com/dataflows-au/${repo.name}`}
                target="_blank"
                rel="noreferrer noopener"
                className="ih-card"
              >
                <div className="ih-card-icon"><GitHubIcon /></div>
                <h3 className="ih-card-title dev-repo-name">{repo.name}</h3>
                <p className="ih-card-desc">{repo.description}</p>
                <span className="dev-repo-lang">dataflows-au{repo.language && ` · ${repo.language}`}</span>
                <span className="ih-card-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="ih-cta dev-divided">
        <div className="ih-inner">
          <h2>Ready to add SMS to your app?</h2>
          <p>Create your account, then find your API token on the Developers page of your dashboard.</p>
          <GetApiKeyButton />
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Developers
